import React, { forwardRef, useEffect, useState, useImperativeHandle } from "react";
import { Unity, useUnityContext } from "react-unity-webgl";

const MarsExploration = forwardRef((props, ref) => {
  const {
    unityProvider,
    loadingProgression,
    isLoaded,
    unload,
    sendMessage,
    addEventListener,
    removeEventListener,
  } = useUnityContext({
    loaderUrl: "/marsexploration_webgl/Build/marsexploration_webgl.loader.js",
    dataUrl: "/marsexploration_webgl/Build/marsexploration_webgl.data",
    frameworkUrl: "/marsexploration_webgl/Build/marsexploration_webgl.framework.js",
    codeUrl: "/marsexploration_webgl/Build/marsexploration_webgl.wasm",
    streamingAssetsUrl: "/marsexploration_webgl/StreamingAssets",
  });

  const [roverStatus, setRoverStatus] = useState(null);
  const [error, setError] = useState(null);
  const [roverModelReady, setRoverModelReady] = useState(false);

  useEffect(() => {
    fetch("/data/mars_rover_status.json")
      .then((res) => res.json())
      .then((json) => setRoverStatus(json.data))
      .catch((err) => setError(err.message));
  }, []);

  useImperativeHandle(ref, () => ({
    shutdown: async () => {
      try {
        await unload();
        console.log("Mars Unity instance unloaded successfully.");
      } catch (e) {
        console.error("Failed to unload Mars Unity instance:", e);
      }
    },
  }));

  // Safety net: if this component unmounts without shutdown() having been
  // called explicitly first, still attempt to release the WebGL context
  // rather than leaking it silently.
  useEffect(() => {
    return () => {
      unload().catch((e) => console.error("Unload on unmount failed:", e));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listen for Unity reporting the rover model finished loading
  useEffect(() => {
    function handleRoverEvent(jsonString) {
      try {
        const payload = JSON.parse(jsonString);
        if (payload.loaded && payload.model === "rover") {
          setRoverModelReady(true);
        }
        if (payload.loaded === false) {
          console.error("Rover failed to load in Unity:", payload.error);
        }
      } catch (e) {
        console.error("Failed to parse Unity event payload:", e);
      }
    }

    addEventListener("OnRoverEvent", handleRoverEvent);
    return () => removeEventListener("OnRoverEvent", handleRoverEvent);
  }, [addEventListener, removeEventListener]);

  // Once Unity is loaded AND the rover model is ready AND we have status
  // data, push it into the running Unity scene.
  useEffect(() => {
    if (isLoaded && roverModelReady && roverStatus) {
      sendMessage("RoverController", "SetRoverStatus", JSON.stringify(roverStatus));
    }
  }, [isLoaded, roverModelReady, roverStatus, sendMessage]);

  return (
    <div style={{ width: "100%", height: "100%", position: "relative", color: "white" }}>
      {!isLoaded && (
        <div style={{
          position: "absolute", top: 0, left: 0, width: "100%", height: "100%",
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
          background: "rgba(0,0,0,0.8)", zIndex: 1,
        }}>
          <p>Loading Mars Exploration... {Math.round(loadingProgression * 100)}%</p>
        </div>
      )}

      {error && (
        <p style={{ position: "absolute", top: 10, left: 10, color: "red", zIndex: 2 }}>
          Failed to load rover status: {error}
        </p>
      )}

      {roverStatus && (
        <div className="rover-health-panel" style={{
          position: "absolute", top: 10, right: 10, zIndex: 2,
          background: "rgba(0,0,0,0.6)", padding: "0.75rem 1rem", borderRadius: "8px",
        }}>
          <p><strong>Spacecraft:</strong> {roverStatus.name}</p>
          <p><strong>Status:</strong> {roverStatus.status}</p>
          <p><strong>Latest Sol:</strong> {roverStatus.max_sol} (as of {roverStatus.max_date})</p>
          <p><strong>Mission Day:</strong> Landed {roverStatus.landing_date}</p>
          <p style={{ fontSize: "0.8em", opacity: 0.7 }}>
            Based on latest published NASA mission data — not live real-time telemetry.
          </p>
        </div>
      )}

      <Unity unityProvider={unityProvider} style={{ width: "100%", height: "100%" }} />
    </div>
  );
});

export default MarsExploration;